import { useIdle } from '@vueuse/core'
import { watch } from 'vue'
import auth from '@/utils/auth'
import axios from 'axios'
import router from '@/router'

export const IDLE_LIMIT_MS = 4 * 60 * 60 * 1000 // 4 hours
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000
const ACTIVITY_EVENTS = ['click', 'keydown', 'touchstart', 'mousemove', 'scroll']

let lastHeartbeatAt = 0

// Confirms to the API that a real user is still interacting, independent of
// whatever business API calls they may or may not be making (e.g. someone
// filling in a long form for a while before hitting save) - otherwise the
// server-side idle check would only ever see the eventual save request and
// could reject it as coming from a stale/idle session.
//
// Uses the `auth` singleton directly rather than the `useAuth()` composable:
// `useAuth()` relies on Vue's `inject()`, which only works inside `setup()`
// (or contexts Vue Router explicitly wraps, like navigation guards) - it
// silently returns undefined here, since this runs from a plain DOM event
// listener.
function sendHeartbeat () {
  const now = Date.now()
  const msSinceLast = now - lastHeartbeatAt

  // TEMPORARY - remove once heartbeat timing is confirmed working
  if (msSinceLast < HEARTBEAT_INTERVAL_MS) {
    console.debug(`[idle-logout] heartbeat skipped - ${Math.round((HEARTBEAT_INTERVAL_MS - msSinceLast) / 1000)}s until next allowed`)
    return
  }
  if (!auth.check()) {
    console.debug('[idle-logout] heartbeat skipped - not authenticated')
    return
  }

  lastHeartbeatAt = now
  console.debug('[idle-logout] sending heartbeat')
  axios.post('/heartbeat').catch(() => {})
}

export function useIdleLogout () {
  const { idle } = useIdle(IDLE_LIMIT_MS)

  ACTIVITY_EVENTS.forEach(event => {
    window.addEventListener(event, sendHeartbeat, { passive: true })
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      sendHeartbeat()
    }
  })

  watch(idle, async (isIdle) => {
    if (!isIdle) return
    if (!auth.check()) return

    await auth.logout()

    if (window.location.pathname !== '/login') {
      router.replace('/login')
    }
  })
}
